import { FC, useEffect, useMemo, useRef, useState } from 'react';
import { IoClose } from 'react-icons/io5';
import { useTranslation } from 'react-i18next';
import Fuse from 'fuse.js';
import OptionButton from '../buttons/OptionButton';
import { sizes as RAW_SIZES } from '../../common/data/sizes';

export type Size = { label: string };

interface SizeSearchInputProps {
  /** Sizes returned by your API for the selected product/brand */
  recommendedSizes: Size[];
  /** Loading state while fetching recommended sizes */
  loading?: boolean;
  /** Called when a size is selected */
  onSelect: (size: Size) => void;
  /** Optional extra classes for the wrapper */
  className?: string;
  /** Limit of search results when typing (default 10) */
  limit?: number;
}

function debounce<T extends (...args: any[]) => void>(
  func: T,
  wait: number
): (...args: Parameters<T>) => void {
  let timeout: NodeJS.Timeout | null = null;
  return function (...args: Parameters<T>) {
    if (timeout) clearTimeout(timeout);
    timeout = setTimeout(() => func(...args), wait);
  };
}

/** Normalize any raw sizes dataset into [{label: string}] */
function normalizeSizes(raw: any[]): Size[] {
  if (!raw || raw.length === 0) return [];
  if (typeof raw[0] === 'string') return raw.map((s) => ({ label: s as string }));
  if ('sizeLabel' in raw[0]) return raw.map((s: any) => ({ label: s.sizeLabel }));
  if ('label' in raw[0]) return raw as Size[];
  // fallback: best-effort stringify
  return raw.map((s) => ({ label: String(s) }));
}

const ALL_SIZES: Size[] = normalizeSizes(RAW_SIZES);

const SizeSearchInput: FC<SizeSearchInputProps> = ({
  recommendedSizes,
  loading = false,
  onSelect,
  className = '',
  limit = 10,
}) => {
  const { t } = useTranslation();

  const [query, setQuery] = useState('');
  const [isSearching, setIsSearching] = useState(false);
  const [otherSizes, setOtherSizes] = useState<Size[]>([]);

  // For fast exclusion of recommended sizes from “otherSizes”
  const recommendedSet = useMemo(
    () => new Set(recommendedSizes.map((s) => s.label)),
    [recommendedSizes]
  );

  // Build Fuse index once
  const fuse = useMemo(() => {
    return new Fuse(ALL_SIZES, {
      keys: ['label'],
      includeScore: true,
      threshold: 0.3,
      ignoreLocation: true,
    });
  }, []);

  const runSearch = (text: string) => {
    if (!text.trim()) {
      setOtherSizes([]);
      setIsSearching(false);
      return;
    }
    const results = fuse.search(text).slice(0, limit);
    // Exclude recommended sizes and dedupe
    const unique: Size[] = [];
    const seen = new Set<string>();
    for (const r of results) {
      const lbl = r.item.label;
      if (recommendedSet.has(lbl)) continue;
      if (seen.has(lbl)) continue;
      seen.add(lbl);
      unique.push({ label: lbl });
    }
    setOtherSizes(unique);
    setIsSearching(false);
  };

  const debounced = useRef(
    debounce((text: string) => runSearch(text), 250)
  ).current;

  useEffect(() => {
    return () => {
      //debounced.cancel();
    };
  }, [debounced]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const text = e.target.value;
    setQuery(text);
    setIsSearching(!!text.trim());
    debounced(text);
  };

  const clear = () => {
    setQuery('');
    setOtherSizes([]);
    setIsSearching(false);
  };

  const renderGrid = (sizesToRender: Size[]) => {
    if (!sizesToRender || sizesToRender.length === 0) return null;
    return (
      <div className="pop-grid pop-grid-cols-5 pop-gap-3">
        {sizesToRender.map((s) => (
          <OptionButton
            key={s.label}
            className="pop-rounded-full pop-fadeIn"
            onClick={() => onSelect(s)}
          >
            {s.label}
          </OptionButton>
        ))}
      </div>
    );
  };

  return (
    <div className={`pop-w-full pop-flex pop-flex-col pop-items-center ${className}`}>
      {/* Search input */}
      <div className="pop-relative pop-w-full pop-mb-4">
        <input
          type="text"
          value={query}
          onChange={handleChange}
          placeholder={t('size_search_placeholder') as string}
          className="pop-w-full pop-text-center pop-border-1 pop-border-popsizeSecondary90 pop-rounded-full pop-p-2 pop-text-popsizePrimary90 focus:pop-outline-none pop-bg-white"
        />
        {query && (
          <button
            type="button"
            onClick={clear}
            className="pop-absolute pop-right-4 pop-top-1/2 -pop-translate-y-1/2 pop-text-popsizePrimary90"
            aria-label={t('clear') as string}
          >
            <IoClose size={18} />
          </button>
        )}
      </div>

      {/* Loader */}
      {(loading || isSearching) && (
        <div className="pop-flex pop-flex-col pop-items-center pop-justify-center pop-my-6">
          <span className="pop-loading pop-loading-dots pop-loading-lg pop-bg-popsizeGradientSecondary9050" />
          <div className="pop-text-popsizePrimary90 pop-mt-2 pop-text-sm">
            {t('size_label_loader_text')}
          </div>
        </div>
      )}

      {/* Results */}
      {!loading && !isSearching && (
        <>
          {query.trim() ? (
            // “Other sizes” when searching
            <>
              {otherSizes.length > 0 ? (
                renderGrid(otherSizes)
              ) : (
                <div className="pop-text-popsizePrimary50 pop-text-sm pop-my-4">
                  {t('no_results_found')}
                </div>
              )}
            </>
          ) : (
            // Recommended sizes when idle (API results)
            <>
              {recommendedSizes.length > 0 ? (
                renderGrid(recommendedSizes)
              ) : (
                <div className="pop-text-popsizePrimary50 pop-text-sm pop-my-4">
                  {t('no_size_available')}
                </div>
              )}
            </>
          )}
        </>
      )}
    </div>
  );
};

export default SizeSearchInput;
